import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import {
 ChevronDown,
 Upload,
 Clock,
 Sparkles,
 Mail,
 Mic,
 Package,
 ClipboardList,
 Search,
 Receipt,
 PawPrint,
 Phone,
 HelpCircle,
 ArrowRight,
 Camera,
} from "lucide-react";
import { CAPTURE_TIP } from "@/data/guideTips";

/**
 * Guide.jsx. TenantTrail Help Center
 *
 * A calm, scrollable index of "how do I…" answers. No tour, no popups.
 * Users come here when they want to learn. they don't get interrupted
 * while they're trying to get a job done.
 */

const SECTIONS = [
 {
 id: "getting-started",
 Icon: Upload,
 title: "Getting started",
 blurb: "Create a case, set your state, drop in your first documents.",
 steps: [
 "Click + New case from your dashboard. Pick the state your rental is in. this tells Compass which Residential Tenancies Act to read.",
 "Open the case, then go to the Documents tab and drop your lease into the upload area. Don't worry about naming. Compass reads the PDF and extracts dates, bond and parties.",
 "Add a photo of your bond receipt. The EXIF data is preserved as a forensic timestamp.",
 "Use the onboarding wizard's checkboxes to switch on the optional tabs you need: Pets, Rent ledger, Contacts.",
 ],
 },
 {
 id: "documents",
 Icon: Upload,
 title: "Documents, the vault",
 blurb: "PDFs, photos, screenshots, emails. One pile or many.",
 steps: [
 "Drop a single file: pick a document type and (optionally) a date, then drop it in. Compass will summarise it for you.",
 "Drop many files at once: just select them all. Compass batches them, reads each one and shows you a review modal where you can tweak titles and dates before saving.",
 "Each document gets an Annex number based on when it actually happened (date order). Annex numbers are stable. they're how Compass and your letters reference evidence.",
 "Photos: if your phone preserved EXIF, you'll see a green Taken: tag and (sometimes) GPS coordinates. Tap Forensic check to inspect deeper.",
 "Delete: click the trash icon. Documents are gone immediately and the chronology re-numbers.",
 ],
 },
 {
 id: "chronology",
 Icon: Clock,
 title: "Chronology, order is half the battle",
 blurb: "Visual timeline + tribunal-ready evidence bundle.",
 steps: [
 "Documents with dates are placed automatically. Use + Add event for things without a document (phone calls, conversations).",
 "The timeline ruler is interactive: drag a document if a date is wrong. Lock the chronology before tribunal so nothing moves by accident.",
 "Click Build evidence bundle PDF to generate a single PDF: cover page, indexed contents, every document appended in order. This is what you file.",
 ],
 },
 {
 id: CAPTURE_TIP.id,
 Icon: Camera,
 title: CAPTURE_TIP.title,
 blurb: CAPTURE_TIP.blurb,
 body: CAPTURE_TIP.body,
 steps: CAPTURE_TIP.steps,
 },
 {
 id: "compass",
 Icon: Sparkles,
 title: "Ask Compass, AI on your tenancy",
 blurb: "Our Deep AI + the Act + your documents.",
 steps: [
 "Open the Ask Compass tab. Compass has already read every uploaded document and the Residential Tenancies Act for your state.",
 "Every answer cites the specific Act section. e.g. s 217 RTRA Act 2008 (QLD). Click section links to read the actual statute.",
 "Annex references (Annex 4, etc.) link back to the document they refer to.",
 "Free tier gets 5 queries to try; paid tiers get monthly allowances. See the queries left counter in the chat header.",
 "Not legal advice. Compass points you to your state's free Tenants' Union when things get serious.",
 ],
 },
 {
 id: "voice",
 Icon: Mic,
 title: "Voice → Statement",
 blurb: "Talk through what happened; Compass writes the statement.",
 steps: [
 "Click the Voice → Statement card on the case page.",
 "Hit record and tell the story in your own words. It's just for you. there's no audience.",
 "Compass transcribes, then shapes the transcript into a tribunal-ready statement that references your annexes.",
 "Edit the draft as much as you like. Export to PDF for your bundle.",
 ],
 },
 {
 id: "letters",
 Icon: Mail,
 title: "Letters: draft and send",
 blurb: "Templates → polished letters → real email delivery.",
 steps: [
 "Open the Letters tab. Pick a template: Repair request, Bond dispute, Notice to remedy breach, etc.",
 "Compass drafts the letter using your case facts and the right Act citations. Review and edit.",
 "Send via email: enter the agent's address. We send via Resend with delivery + open tracking and a unique Reply-To address so replies land back in TenantTrail.",
 "Every send is logged with an audit timestamp. useful evidence if the agent later claims they never received it.",
 ],
 },
 {
 id: "inspections",
 Icon: ClipboardList,
 title: "Inspections, entry & exit reports",
 blurb: "Upload a condition report; Compass extracts every row.",
 steps: [
 "Pick entry or exit, then upload the PDF or photo of the report.",
 "Compass extracts every room + item row and pre-ticks anything that looks like a tenant disagreement.",
 "Add your own notes, tick additional items. Click Raised with agent ✓ to stamp the date and start the 7-day chase clock.",
 "Paste the agent's response when it arrives. the tag goes green. Done.",
 ],
 },
 {
 id: "rent",
 Icon: Receipt,
 title: "Rent ledger",
 blurb: "Track every payment, evidence rent-arrears claims.",
 steps: [
 "Open the case's Rent ledger tab (switched on during onboarding).",
 "Log each payment with the date, amount and method. Compass calculates running totals and flags missed weeks.",
 "Export the ledger as PDF for your evidence bundle if rent is part of your dispute.",
 ],
 },
 {
 id: "pets",
 Icon: PawPrint,
 title: "Pets",
 blurb: "Pet bond, approvals, photos.",
 steps: [
 "Add each pet with type, breed and any approval documents.",
 "If your state allows a pet bond, the Compass dashboard surfaces relevant deadlines.",
 ],
 },
 {
 id: "contacts",
 Icon: Phone,
 title: "Contacts",
 blurb: "Landlord, agent, tribunal, locksmith, in one place.",
 steps: [
 "Add your landlord, agent (and agency), Tenants' Union, RTA/Bond Authority contacts.",
 "Compass uses these when drafting letters so you don't have to retype email addresses.",
 ],
 },
 {
 id: "property-check",
 Icon: Search,
 title: "Property History Check",
 blurb: "Before you sign, look up the rental.",
 steps: [
 "From the Navbar, click Check a rental.",
 "Paste the address. Compass searches AustLII for tribunal records and produces a plain-English summary.",
 "Use it to spot landlords or agents with a track record of disputes before you commit.",
 ],
 },
];

const FAQ = [
 {
 q: "Is this legal advice?",
 a: "No. Compass is an AI assistant that helps you organise evidence and understand the Residential Tenancies Act in your state. For legal advice, contact your state's free Tenants' Union (Compass will link you to the right one).",
 },
 {
 q: "Where are my documents stored?",
 a: "On servers we operate, encrypted in transit. Documents are linked to your account only and are never used to train public AI models. See the Privacy page for the full picture.",
 },
 {
 q: "Can I export everything?",
 a: "Yes. Build evidence bundle PDF gives you a tribunal-ready file. You can also download any individual document at any time. If you want a full account export, email support.",
 },
 {
 q: "What does each plan get me?",
 a: "Free has 5 AI queries to test. Vault is lifetime evidence storage. Compass adds 100 AI queries/month + letter drafting + voice statements + bundles. Dispute Mode is a 30-day burst (500 queries) for when you're heading to tribunal. Pro gives 750 queries/month + 50 GB storage. See the Pricing page for AUD amounts.",
 },
 {
 q: "Why is the chronology locked?",
 a: "Before tribunal, locking prevents accidental drag-and-drop changes to dates. Unlock from the Chronology tab if you need to make corrections. every edit is timestamped.",
 },
 {
 q: "What if the AI gets it wrong?",
 a: "Compass cites section numbers so you can verify every claim against the actual Act. If something looks off, click the citation and read the source. Tell us when it's wrong. every flag improves the system.",
 },
];

function SectionCard({ s }) {
 const [open, setOpen] = useState(false);
 const Icon = s.Icon;
 return (
 <div className="card" data-testid={`guide-section-${s.id}`}>
 <button
 onClick={() => setOpen(o => !o)}
 className="w-full flex items-start gap-4 text-left"
 data-testid={`guide-toggle-${s.id}`}
 >
 <div className="w-10 h-10 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30 flex-shrink-0">
 <Icon size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 </div>
 <div className="flex-1 min-w-0">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-0.5">{s.title}</h3>
 <p className="text-sm text-[#6B7280] leading-snug">{s.blurb}</p>
 </div>
 <ChevronDown
 size={18}
 strokeWidth={1.5}
 className={`text-[#6B7280] flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
 />
 </button>
 {open && (
 <>
 {s.body && (
 <div className="mt-5 pl-14 space-y-3 text-sm text-[#1A1A1A] leading-relaxed font-serif">
 {s.body.map((para, i) => (
 <p key={i}>{para}</p>
 ))}
 </div>
 )}
 <ol className="mt-5 space-y-3 pl-14 list-decimal text-sm text-[#1A1A1A] leading-relaxed marker:text-[#1B6C62] marker:font-mono">
 {s.steps.map((step, i) => (
 <li key={i} className="font-serif">{step}</li>
 ))}
 </ol>
 </>
 )}
 </div>
 );
}

function FaqItem({ item, i }) {
 const [open, setOpen] = useState(false);
 return (
 <div className="border-b border-[#E5E4DE] py-4" data-testid={`guide-faq-${i}`}>
 <button
 onClick={() => setOpen(o => !o)}
 className="w-full flex justify-between items-start gap-4 text-left"
 data-testid={`guide-faq-toggle-${i}`}
 >
 <h4 className="font-serif text-[15px] text-[#0D2B52]">{item.q}</h4>
 <ChevronDown
 size={16}
 strokeWidth={1.5}
 className={`text-[#6B7280] flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
 />
 </button>
 {open && <p className="mt-3 text-sm font-serif text-[#1A1A1A] leading-relaxed">{item.a}</p>}
 </div>
 );
}

export default function Guide() {
 return (
 <div className="min-h-screen paper-grain" data-testid="guide-page">
 <Navbar />
 <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
 <div className="mb-12">
 <div className="label-mono mb-3">Help · Guide</div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">How TenantTrail works</h1>
 <p className="text-[#333] font-serif text-base leading-relaxed max-w-2xl">
 Calm, step-by-step answers to the things people actually ask us. Click a section to
 expand. Nothing here is legal advice. For that, your state&apos;s Tenants&apos; Union is free.
 </p>
 </div>

 {/* Table of contents */}
 <div className="card mb-10 bg-[#FAF9F6]">
 <div className="label-mono mb-3">On this page</div>
 <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
 {SECTIONS.map(s => (
 <a
 key={s.id}
 href={`#${s.id}`}
 className="text-[#0D2B52] hover:text-[#1B6C62] inline-flex items-center gap-2"
 data-testid={`guide-toc-${s.id}`}
 >
 <s.Icon size={13} strokeWidth={1.5} className="text-[#1B6C62]" /> {s.title}
 </a>
 ))}
 <a
 href="#faq"
 className="text-[#0D2B52] hover:text-[#1B6C62] inline-flex items-center gap-2"
 data-testid="guide-toc-faq"
 >
 <HelpCircle size={13} strokeWidth={1.5} className="text-[#1B6C62]" /> FAQ
 </a>
 </div>
 </div>

 <div className="space-y-4 mb-14">
 {SECTIONS.map(s => (
 <div key={s.id} id={s.id} className="scroll-mt-24">
 <SectionCard s={s} />
 </div>
 ))}
 </div>

 <div id="faq" className="scroll-mt-24 mb-14">
 <div className="flex items-center gap-2 mb-4">
 <HelpCircle size={20} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h2 className="font-serif text-2xl text-[#0D2B52]">Frequently asked</h2>
 </div>
 <div className="card">
 {FAQ.map((item, i) => (
 <FaqItem key={i} item={item} i={i} />
 ))}
 </div>
 </div>

 <div className="card bg-[#E6F5F2]/40 border-l-4 border-l-[#1B6C62]">
 <div className="label-mono mb-2 text-[#1B6C62]">Still stuck?</div>
 <h3 className="font-serif text-xl text-[#0D2B52] mb-2">We&apos;re a real team. Email us.</h3>
 <p className="text-sm text-[#1A1A1A] font-serif leading-relaxed mb-4">
 We answer support email within one business day, often sooner. If you&apos;re heading to
 tribunal this week, say so in the subject line. We&apos;ll bump it.
 </p>
 <div className="flex flex-wrap gap-3">
 <Link
 to="/support"
 className="btn-primary inline-flex items-center gap-2 text-sm"
 data-testid="guide-support-cta"
 >
 Contact support <ArrowRight size={14} strokeWidth={1.5} />
 </Link>
 <Link
 to="/ask"
 className="btn-secondary inline-flex items-center gap-2 text-sm"
 data-testid="guide-brain-cta"
 >
 Ask the free Brain
 </Link>
 </div>
 </div>
 </div>
 
      <SiteFooter />
      </div>
 );
}
